import {useIssueState} from '@github-ui/issue-pr-state'
import type {PullRequestStateType} from '@github-ui/issue-pr-state/constants'
import {issueLinkedPullRequestHovercardPath} from '@github-ui/paths'
import {Link} from '@primer/react'
import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {LABELS} from '../constants/labels'
import {createIssueEventExternalUrl} from '../utils/urls'
import type {DisconnectedEvent$key} from './__generated__/DisconnectedEvent.graphql'
import styles from './DisconnectedEvent.module.css'
import {TimelineRow} from './row/TimelineRow'

type DisconnectedEventProps = {
  queryRef: DisconnectedEvent$key
  issueUrl: string
  onLinkClick?: (event: MouseEvent) => void
  highlightedEventId?: string
  refAttribute?: React.MutableRefObject<HTMLDivElement | null>
}

export function DisconnectedEvent({
  queryRef,
  issueUrl,
  onLinkClick,
  highlightedEventId,
  refAttribute,
}: DisconnectedEventProps): React.ReactElement {
  const {actor, createdAt, subject, databaseId} = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment DisconnectedEvent on DisconnectedEvent {
        databaseId
        actor {
          ...TimelineRowEventActor
        }
        createdAt
        subject {
          ... on PullRequest {
            title
            url
            number
            state
            isDraft
            isInMergeQueue
            repository {
              name
              owner {
                login
              }
            }
          }
        }
      }
    `,
    queryRef,
  )

  const {title, number, url, isDraft, isInMergeQueue, state, repository} = subject || {}

  const {sourceIcon: getSourceIcon} = useIssueState({state: state as PullRequestStateType})
  const PullStateIcon = getSourceIcon('PullRequest', isDraft, isInMergeQueue)
  const highlighted = String(databaseId) === highlightedEventId

  const dataHovercardUrl =
    repository && number
      ? issueLinkedPullRequestHovercardPath({
          owner: repository?.owner.login,
          repo: repository?.name,
          pullRequestNumber: number,
        })
      : null

  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={createdAt}
      deepLinkUrl={createIssueEventExternalUrl(issueUrl, databaseId)}
      onLinkClick={onLinkClick}
      leadingIcon={PullStateIcon}
    >
      <TimelineRow.Main>
        {LABELS.timeline.removedLinkedPR}
        {/* eslint-disable-next-line react-hooks/static-components */}
        <PullStateIcon className="ml-1 mr-1" />
        <Link
          href={url}
          target="_blank"
          data-hovercard-url={dataHovercardUrl}
          inline
          className={styles.disconnectedPullRequestLink}
        >
          {`${title} #${number}`}
        </Link>
      </TimelineRow.Main>
    </TimelineRow>
  )
}

try{ DisconnectedEvent.displayName ||= 'DisconnectedEvent' } catch {}
try{ PullStateIcon.displayName ||= 'PullStateIcon' } catch {}